/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Branding } from "../../branding";
import { BrandingUiInfoKeys } from "../../entities";
import { Locale, LocaleKeys } from "../../locale";
import { playerErrorCodes } from "./PlayerErrorCodes";

/**
 * User-facing title and description for a V-code, sourced verbatim from the
 * `title`/`body` fields of the code's YAML definition in
 * https://github.com/tivocorp/unity-client-error-codes/tree/main/codes/V.
 */
export interface WebErrorCodeMessage {
  readonly title: string;
  readonly description: string;
}

/**
 * Build the user-facing titles and descriptions for the V-codes
 * {@link getWebErrorCode} can produce, copied verbatim from the
 * `title`/`body` fields of each code's YAML definition in
 * https://github.com/tivocorp/unity-client-error-codes/tree/main/codes/V.
 *
 * A code whose YAML `title`/`body` is itself a placeholder
 * (`$(MSG_V549_TITLE)`/`$(MSG_V549_BODY)`, carried by every code V549 was
 * split into: V634-V643) resolves to V549's own title/body text below.
 *
 * Built fresh on every call rather than cached in a module-level constant:
 * `Locale.getString` reads from a language map that {@link Locale.setLocale}
 * populates asynchronously, so resolving these strings once at module-import
 * time can run before the locale has loaded and freeze every entry as `""`.
 * Callers ({@link PlaybackErrorMapper.fromPlayerError}) look codes up by the
 * base V-code (the part of the compound `Vxxx-NNN` code before the
 * separator).
 */
export function getWebErrorCodeDescriptions(): ReadonlyMap<
  string,
  WebErrorCodeMessage
> {
  // Shared title text for V550/V551/V555/V560/V565 — every one of these
  // codes' own YAML carries the generic `title: Player Error`.
  const genericPlayerErrorTitle = Locale.getString(
    LocaleKeys.PLAYER_ERROR_GENERIC_TITLE
  );

  // Shared title text for V561/V562/V563/V564 — every one of these codes'
  // own YAML carries the generic `title: Decoder Error`.
  const decoderErrorTitle = Locale.getString(
    LocaleKeys.PLAYER_ERROR_DECODER_ERROR_TITLE
  );

  // Shared title text for V593/V594 — every one of these codes' own YAML
  // carries the generic `title: DRM Error`.
  const drmErrorTitle = Locale.getString(LocaleKeys.PLAYER_ERROR_DRM_TITLE);

  // Shared title text for V634/V635/V636/V638/V639/V641/V642 — each of these
  // codes' own YAML carries `$(MSG_V549_TITLE)`, a placeholder for the
  // deprecated V549's title text they supersede.
  const v549Title = Locale.getString(LocaleKeys.PLAYER_ERROR_UNAVAILABLE_TITLE);

  // Shared body text for V634/V635/V636/V638/V639/V641/V642 — each of these
  // codes' own YAML carries `$(MSG_V549_BODY)`, a placeholder for the
  // deprecated V549's body text they supersede.
  const v549Body = Locale.getString(
    LocaleKeys.PLAYER_ERROR_UNAVAILABLE_MESSAGE
  );

  return new Map([
    [
      playerErrorCodes.PLAYER_BAD_INPUT,
      {
        title: genericPlayerErrorTitle,
        description: Locale.getString(LocaleKeys.PLAYER_ERROR_BAD_INPUT_MESSAGE)
      }
    ],
    [
      playerErrorCodes.PLAYER_PARSER_FAILURE,
      {
        title: genericPlayerErrorTitle,
        description: Locale.getString(
          LocaleKeys.PLAYER_ERROR_KNOWN_ISSUE_MESSAGE
        )
      }
    ],
    [
      playerErrorCodes.PLAYER_SOURCE_EXCEPTION,
      {
        title: genericPlayerErrorTitle,
        description: Locale.getString(
          LocaleKeys.PLAYER_ERROR_OPEN_READ_FAILED_MESSAGE
        )
      }
    ],
    [
      playerErrorCodes.PLAYER_ILLEGAL_STATE,
      {
        title: genericPlayerErrorTitle,
        description: Locale.getString(
          LocaleKeys.PLAYER_ERROR_KNOWN_ISSUE_ALL_PROGRAMS_MESSAGE
        ).replace(
          "{{deviceNameShort}}",
          Branding.getFallbackBrandingUiInfoForKey(
            BrandingUiInfoKeys.DEVICE_NAME_SHORT
          ) as string
        )
      }
    ],
    [
      playerErrorCodes.DECODER_QUERY_FAILED,
      {
        title: decoderErrorTitle,
        description: Locale.getString(
          LocaleKeys.PLAYER_ERROR_DECODER_QUERY_FAILED_MESSAGE
        )
      }
    ],
    [
      playerErrorCodes.DECODER_INITIALIZATION_FAILED,
      {
        title: decoderErrorTitle,
        description: Locale.getString(
          LocaleKeys.PLAYER_ERROR_DECODER_INIT_FAILED_MESSAGE
        )
      }
    ],
    [
      playerErrorCodes.DECODER_CRYPTO_FAILED,
      {
        title: decoderErrorTitle,
        description: Locale.getString(
          LocaleKeys.PLAYER_ERROR_DECODER_CRYPTO_FAILED_MESSAGE
        )
      }
    ],
    [
      playerErrorCodes.SUBTITLE_DECODER_FAILED,
      {
        title: decoderErrorTitle,
        description: Locale.getString(
          LocaleKeys.PLAYER_ERROR_SUBTITLE_DECODER_FAILED_MESSAGE
        )
      }
    ],
    [
      playerErrorCodes.PLAYER_RENDER_FAILED,
      {
        title: genericPlayerErrorTitle,
        description: Locale.getString(
          LocaleKeys.PLAYER_ERROR_RENDER_FAILED_MESSAGE
        )
      }
    ],
    [
      playerErrorCodes.PLAYER_DRM_OUTPUT_PROTECTION_ERROR,
      {
        title: drmErrorTitle,
        description: Branding.applyCustomerSupportPlaceholders(
          Locale.getString(
            LocaleKeys.PLAYER_ERROR_DRM_OUTPUT_PROTECTION_MESSAGE
          )
        )
      }
    ],
    [
      playerErrorCodes.PLAYER_DRM_SECURITY_LEVEL_ERROR,
      {
        title: drmErrorTitle,
        description: Locale.getString(
          LocaleKeys.PLAYER_ERROR_DRM_SECURITY_MESSAGE
        )
      }
    ],
    // V634/V635/V636/V638/V639/V641/V642 supersede the deprecated V549 and
    // carry forward its title/body verbatim ($(MSG_V549_TITLE)/
    // $(MSG_V549_BODY) in their own YAML).
    [
      playerErrorCodes.DRM_DECRYPTION_KEY_MISSING,
      { title: v549Title, description: v549Body }
    ],
    [
      playerErrorCodes.MANIFEST_SOCKET_OPEN_ERROR,
      { title: v549Title, description: v549Body }
    ],
    [
      playerErrorCodes.MANIFEST_INVALID_HTTP_RESPONSE,
      { title: v549Title, description: v549Body }
    ],
    [
      playerErrorCodes.SEGMENT_SOCKET_OPEN_ERROR,
      { title: v549Title, description: v549Body }
    ],
    [
      playerErrorCodes.SEGMENT_INVALID_HTTP_RESPONSE,
      { title: v549Title, description: v549Body }
    ],
    [
      playerErrorCodes.SOCKET_OPEN_EXCEPTION,
      { title: v549Title, description: v549Body }
    ],
    [
      playerErrorCodes.INVALID_HTTP_RESPONSE,
      { title: v549Title, description: v549Body }
    ],
    [
      playerErrorCodes.DRM_SYSTEM_ERROR,
      {
        title: Locale.getString(LocaleKeys.PLAYER_ERROR_DRM_SYSTEM_ERROR_TITLE),
        description: Branding.applyCustomerSupportPlaceholders(
          Locale.getString(LocaleKeys.PLAYER_ERROR_DRM_SYSTEM_ERROR_MESSAGE)
        )
      }
    ],
    [
      playerErrorCodes.DRM_NOT_SUPPORTED_ERROR,
      {
        title: Locale.getString(
          LocaleKeys.PLAYER_ERROR_DRM_SCHEME_UNSUPPORTED_TITLE
        ),
        description: Branding.applyCustomerSupportPlaceholders(
          Locale.getString(
            LocaleKeys.PLAYER_ERROR_DRM_SCHEME_UNSUPPORTED_MESSAGE
          )
        )
      }
    ],
    [
      playerErrorCodes.DRM_CONTENT_ERROR,
      {
        title: Locale.getString(
          LocaleKeys.PLAYER_ERROR_DRM_INCOMPATIBLE_CONTENT_TITLE
        ),
        description: Branding.applyCustomerSupportPlaceholders(
          Locale.getString(
            LocaleKeys.PLAYER_ERROR_DRM_INCOMPATIBLE_CONTENT_MESSAGE
          )
        )
      }
    ],
    [
      playerErrorCodes.DRM_LICENSE_EXPIRED_ERROR,
      {
        title: Locale.getString(
          LocaleKeys.PLAYER_ERROR_DRM_LICENSE_EXPIRED_TITLE
        ),
        description: Branding.applyCustomerSupportPlaceholders(
          Locale.getString(LocaleKeys.PLAYER_ERROR_DRM_LICENSE_EXPIRED_MESSAGE)
        )
      }
    ],
    [
      playerErrorCodes.DRM_LICENSE_ACQUISITION_ERROR,
      {
        title: Locale.getString(
          LocaleKeys.PLAYER_ERROR_DRM_LICENSE_UNAVAILABLE_TITLE
        ),
        description: Branding.applyCustomerSupportPlaceholders(
          Locale.getString(
            LocaleKeys.PLAYER_ERROR_DRM_LICENSE_UNAVAILABLE_MESSAGE
          )
        )
      }
    ],
    [
      playerErrorCodes.DRM_UNSPECIFIED_ERROR,
      {
        title: Locale.getString(LocaleKeys.PLAYER_ERROR_DRM_RESTRICTED_TITLE),
        description: Locale.getString(
          LocaleKeys.PLAYER_ERROR_DRM_RESTRICTED_MESSAGE
        )
      }
    ]
  ]);
}
